import React, { useEffect, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { useTheme } from '../../../../theme-context';

interface Props {
  label: string;
  labelStyle?: string;
  containerStyle?: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
}

const formatDate = (date: Date): string =>
  `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

const SingleDatePopover = ({
  label,
  labelStyle,
  containerStyle,
  value,
  onChange,
}: Props) => {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const [selected, setSelected] = useState<Date | undefined>(value || undefined);

  // Close popover on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync selected date to parent
  useEffect(() => {
    onChange(selected || null);
  }, [selected]);

  const displayText = value ? formatDate(value) : 'mm/dd/yyyy';

  return (
    <div className={`relative w-full ${containerStyle}`} ref={ref}>
      <label className={`block mb-1 text-sm font-medium dark:text-white ${labelStyle}`}>
        {label}
      </label>

      <div
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 border dark:border-[#374151] rounded bg-white dark:bg-black dark:text-white cursor-pointer"
      >
        <span className={`${value ? '' : 'text-primary-light'}`}>{displayText}</span>

        <img
          src={
            theme === 'dark'
              ? '/assets/insights-tab/calendar-white.svg'
              : '/assets/insights-tab/calendar.svg'
          }
          className="object-contain w-[24px]"
        />
      </div>

      {open && (
        <div className="absolute mt-1 z-50 p-4 rounded-md shadow-lg bg-white dark:bg-black dark:text-white border dark:border-gray-700 flex flex-col">
          <DayPicker
            mode="single"
            selected={selected}
            onSelect={setSelected}
            numberOfMonths={1}
            defaultMonth={selected}
            classNames={{
              caption: 'dark:text-white',
              head: 'dark:text-gray-300',
              cell: 'dark:hover:bg-gray-800',
            }}
            modifiersClassNames={{
              selected: 'bg-blue-500 text-white dark:bg-blue-400 dark:text-black',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SingleDatePopover;
